/**
 * Shake-stage booster icons (256×256 logical frames, transparent): Shake, Clear and Shrink.
 * They sit on the gold action squares at 36 px (Shake) and 46 px (Clear, Shrink) and double as
 * reward icons in the daily grid and reward reveal, so each is one bold idea in a few inked
 * parts: no thin lines, no small text, and colours that stand off gold (glass blue, red, sky).
 */
import { celShape, glint, inkStyle } from "../toon-kit.js";
import { ICON_LINE, ICON_DETAIL, TAU, inkedRope, iconCreature, withInkDrop } from "./icon-kit.js";

/** Glass of the shake flask: a touch bluer than the game flask so it holds up on gold. */
var GLASS = { base: "#c8eeff", light: "#ffffff", shade: "#82c8ea" };
var WATER = { base: "#4fbcf0", light: "#9fe2ff", shade: "#2f8fd0" };
var ARROW = { base: "#3fb4ff", light: "#b6e8ff" };
var DROP = 5.5;

// ---- Shake ------------------------------------------------------------------------------

/** Erlenmeyer flask outline in its own frame (lip on top, centred on the origin), grown by g. */
function flaskPath(c, g) {
  var nx = 23 + g, top = -98 - g, sh = -34;
  var bx = 80 + g, by = 62 + g, cr = 28;
  c.moveTo(-nx, top);
  c.lineTo(-nx, sh);
  c.lineTo(-bx + 6, by - cr);
  c.quadraticCurveTo(-bx - 4, by, -bx + cr, by);
  c.lineTo(bx - cr, by);
  c.quadraticCurveTo(bx + 4, by, bx - 6, by - cr);
  c.lineTo(nx, sh);
  c.lineTo(nx, top);
  c.closePath();
}

/** The flask's rolled lip: a short inked capsule over the mouth. */
function flaskLip(c) {
  c.beginPath();
  c.moveTo(-33, -104);
  c.lineTo(33, -104);
  inkedRope(c, 15, GLASS.light, ICON_LINE * 0.62);
  c.beginPath();
  c.moveTo(-24, -106);
  c.lineTo(8, -106);
  c.strokeStyle = "#ffffff";
  c.lineWidth = 5;
  c.stroke();
}

/** Motion arcs either side of the flask: bold inked ropes with a white core. */
function shakeArcs(c, cx, cy) {
  var arcs = [
    [-1, 98, 0.44], [-1, 117, 0.27],
    [1, 98, 0.44], [1, 117, 0.27],
  ];
  for (var i = 0; i < arcs.length; i++) {
    var side = arcs[i][0], rad = arcs[i][1], half = arcs[i][2];
    var mid = side < 0 ? Math.PI + 0.12 : -0.12;
    c.beginPath();
    c.arc(cx, cy, rad, mid - half, mid + half);
    inkedRope(c, 11, "#ffffff", 5.5);
  }
}

function paintShake(c) {
  var cx = 128, cy = 136;
  shakeArcs(c, cx, cy);
  c.save();
  c.translate(cx + 6, cy);
  c.rotate(0.22);
  c.scale(1.1, 1.1);
  // Glass body.
  celShape(c, flaskPath, GLASS, 70, {
    line: ICON_LINE,
    lightPath: function (cc) { cc.ellipse(-46, 6, 26, 60, 0.35, 0, TAU); },
  });
  // Water sloshing toward the low side, with two creatures tumbling in it.
  c.save();
  c.beginPath();
  flaskPath(c, -ICON_LINE * 0.5);
  c.clip();
  var surface = function (cc) {
    cc.moveTo(-110, -4);
    cc.bezierCurveTo(-64, -30, -18, 4, 24, -14);
    cc.bezierCurveTo(56, -28, 88, -18, 110, -36);
  };
  c.beginPath();
  surface(c);
  c.lineTo(110, 100);
  c.lineTo(-110, 100);
  c.closePath();
  c.fillStyle = WATER.shade;
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  c.ellipse(-30, 18, 92, 56, 0, 0, TAU);
  c.fillStyle = WATER.base;
  c.fill();
  c.restore();
  // The surface: a lighter wavy lip.
  c.beginPath();
  surface(c);
  inkStyle(c, 9, WATER.light);
  c.stroke();
  iconCreature(c, "cell", -32, 30, 32, { rot: -0.5, line: 7.5 });
  iconCreature(c, "starfish", 30, 20, 32, { rot: 0.35, line: 7.5, mood: "wow" });
  c.restore();
  // Glass sheen over everything inside: a tall streak down the lit side.
  c.save();
  c.beginPath();
  flaskPath(c, -ICON_LINE * 0.5);
  c.clip();
  c.fillStyle = "rgba(255,255,255,0.85)";
  c.beginPath();
  c.moveTo(-17, -92);
  c.lineTo(-8, -92);
  c.lineTo(-8, -40);
  c.lineTo(-50, 34);
  c.lineTo(-62, 34);
  c.lineTo(-17, -40);
  c.closePath();
  c.fill();
  c.restore();
  c.beginPath();
  flaskPath(c, 0);
  inkStyle(c, ICON_LINE);
  c.stroke();
  flaskLip(c);
  c.restore();
}

/** Shake booster: a tilted flask sloshing two creatures about, with motion arcs. */
export function drawShakeIcon(c, w, h) {
  withInkDrop(c, w, h, DROP, paintShake);
}

// ---- Clear ------------------------------------------------------------------------------

var SIGN = { base: "#ff4f57", light: "#ff9a9a", shade: "#cf2a36" };

/**
 * The no-sign as one closed shape: the ring with two half-moon holes either side of the
 * slash (holes wound the other way, so a plain non-zero fill leaves them open). Drawn in a
 * frame turned 45° so the slash runs top-left to bottom-right.
 */
function signPath(ro, ri, hw) {
  return function (c, g) {
    var o = ro + g, i = Math.max(1, ri - g), h = hw + g;
    var a = Math.asin(Math.min(0.99, h / i));
    c.moveTo(o, 0);
    c.arc(0, 0, o, 0, TAU);
    c.moveTo(i * Math.cos(-a), i * Math.sin(-a));
    c.arc(0, 0, i, -a, -Math.PI + a, true);
    c.closePath();
    c.moveTo(i * Math.cos(Math.PI - a), i * Math.sin(Math.PI - a));
    c.arc(0, 0, i, Math.PI - a, a, true);
    c.closePath();
  };
}

function paintClear(c) {
  var cx = 128, cy = 126;
  var ro = 112, ri = 84, hw = 14;
  // The creatures being cleared, tucked inside the ring.
  iconCreature(c, "starfish", cx + 30, cy - 30, 46, { mood: "wow", line: 8 });
  iconCreature(c, "cell", cx - 30, cy + 30, 46, { mood: "wow", line: 8 });
  c.save();
  c.translate(cx, cy);
  c.rotate(Math.PI / 4);
  var path = signPath(ro, ri, hw);
  // Rotate the light back so it still comes from the upper left of the frame.
  var k = Math.SQRT1_2;
  var shift = [(-0.1 - 0.13) * k, (0.1 - 0.13) * k];
  celShape(c, path, SIGN, ro, {
    // A lighter outline than the other icons': the sign is all outline already.
    line: ICON_LINE * 0.7,
    shift: shift,
    lightPath: function (cc) {
      // A lit arc on the ring's upper-left shoulder (local frame is turned 45°).
      cc.ellipse(-ro * 0.62, -ro * 0.02, ro * 0.36, ro * 0.62, 0, 0, TAU);
    },
  });
  c.restore();
  glint(c, cx - ro * 0.6, cy - ro * 0.55, 9);
}

/** Clear booster: a red inked no-sign over two low-tier creatures. */
export function drawClearIcon(c, w, h) {
  withInkDrop(c, w, h, DROP, paintClear);
}

// ---- Shrink -----------------------------------------------------------------------------

/** A fat arrow along +x with its tip at `tip`: head `hl` long and ±`hw` wide, shaft ±`sw`. */
function arrowPath(c, tip, len, hl, hw, sw) {
  c.beginPath();
  c.moveTo(tip, 0);
  c.lineTo(tip + hl, -hw);
  c.lineTo(tip + hl, -sw);
  c.lineTo(tip + len, -sw);
  c.lineTo(tip + len, sw);
  c.lineTo(tip + hl, sw);
  c.lineTo(tip + hl, hw);
  c.closePath();
}

/** One arrow pointing at the origin along angle `a`, its tip `tip` from the centre. */
function inwardArrow(c, a, tip, len) {
  c.save();
  c.rotate(a);
  var hl = len * 0.56, hw = 26, sw = 10;
  arrowPath(c, tip, len, hl, hw, sw);
  c.fillStyle = ARROW.base;
  c.fill();
  // Light the half of the arrow that faces the frame's upper left.
  var lit = Math.sin(a) - Math.cos(a) >= 0 ? 1 : -1;
  c.save();
  c.clip();
  c.beginPath();
  c.moveTo(tip, 0);
  c.lineTo(tip + len + 4, 0);
  c.lineTo(tip + len + 4, lit * (hw + 4));
  c.lineTo(tip + hl, lit * (hw + 4));
  c.closePath();
  c.fillStyle = ARROW.light;
  c.fill();
  c.restore();
  arrowPath(c, tip, len, hl, hw, sw);
  inkStyle(c, ICON_DETAIL * 1.25);
  c.stroke();
  c.restore();
}

function paintShrink(c) {
  var cx = 128, cy = 126;
  // The dashed target ring, inked like a rope so each dash reads at 46 px.
  c.save();
  c.beginPath();
  c.arc(cx, cy, 110, -Math.PI / 2 + 0.2, Math.PI * 1.5 + 0.2);
  c.setLineDash([30, 24]);
  inkedRope(c, 7, "#bfeaff", 4.5);
  c.restore();
  c.save();
  c.translate(cx, cy);
  for (var i = 0; i < 4; i++) inwardArrow(c, Math.PI / 4 + (i * Math.PI) / 2, 58, 46);
  c.restore();
  iconCreature(c, "starfish", cx, cy, 48, { mood: "wow", line: 8 });
}

/** Shrink booster: a creature bubble squeezed by four arrows inside a dashed ring. */
export function drawShrinkIcon(c, w, h) {
  withInkDrop(c, w, h, DROP, paintShrink);
}

